import React, { useEffect, useMemo, useRef, useState } from 'react';
import { COUNTRIES, type Country, flagUrl } from '../utils/phone';

interface CountrySelectorProps {
  value: Country;
  onChange: (c: Country) => void;
  className?: string;
}

export default function CountrySelector({ value, onChange, className = '' }: CountrySelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus search on open
  useEffect(() => {
    if (open) {
      setSearch('');
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return COUNTRIES;
    const q = search.toLowerCase().trim();
    return COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.code.includes(q) ||
      c.iso.includes(q)
    );
  }, [search]);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex h-12 items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-3 text-sm font-bold text-white/80 hover:bg-white/10 transition whitespace-nowrap"
      >
        <img src={flagUrl(value.iso, 40)} alt={value.name} className="h-4 w-6 rounded-[2px] object-cover" />
        <span>+{value.code}</span>
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" className="ml-0.5 opacity-50">
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-72 rounded-xl border border-white/10 bg-black/95 backdrop-blur-xl shadow-2xl overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-white/10">
            <input
              ref={searchRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search country or code..."
              className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none placeholder:text-white/40 focus:border-[#00E1D7]/50"
            />
          </div>

          {/* List */}
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-white/40">No results</div>
            )}
            {filtered.map((c) => (
              <button
                key={`${c.iso}-${c.code}`}
                type="button"
                onClick={() => { onChange(c); setOpen(false); }}
                className={[
                  'flex w-full items-center gap-2.5 px-3 py-2 text-sm text-white/80 hover:bg-white/10 transition',
                  c.iso === value.iso && c.code === value.code ? 'bg-white/8 font-bold' : ''
                ].join(' ')}
              >
                <img src={flagUrl(c.iso, 40)} alt={c.name} className="h-4 w-6 rounded-[2px] object-cover flex-shrink-0" loading="lazy" />
                <span className="text-white/50 w-12 text-left flex-shrink-0">+{c.code}</span>
                <span className="truncate">{c.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
